import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaPlus,
  FaTrash,
  FaPaperPlane,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaArrowUp,
  FaArrowDown,
  FaEraser,
} from 'react-icons/fa';
import DiscordEmbedPreview from './DiscordEmbedPreview';
import './MessageComposerPanel.css';

const DRAFT_KEY = 'nexusAdminMessageDraft';
const EMBED_SEPARATOR = '{{separator}}';
const EMBED_SEPARATOR_VISUAL = '────────────────────';

const MODES = [
  { id: 'v2', label: 'Container V2' },
  { id: 'both', label: 'Texto + Embed' },
  { id: 'embed', label: 'Só Embed' },
  { id: 'text', label: 'Só Texto' },
];

const BUTTON_STYLES = [
  { value: 'Primary', label: 'Azul (Primary)' },
  { value: 'Secondary', label: 'Cinza (Secondary)' },
  { value: 'Success', label: 'Verde (Success)' },
  { value: 'Danger', label: 'Vermelho (Danger)' },
];

const TEMPLATES = [
  {
    id: 'system',
    name: 'Sistema',
    tagline: 'Container V2',
    color: '#5865F2',
    apply: () => ({
      mode: 'v2',
      content: '',
      embed: {
        title: 'Título do sistema',
        description:
          'Escreva aqui a introdução e as instruções principais.\n\n' +
          'Use o botão Separador para dividir seções com a linha nativa do Discord.\n\n' +
          `${EMBED_SEPARATOR}\n\n` +
          '**Importante**\n' +
          '• Ponto 1\n' +
          '• Ponto 2\n' +
          '• Ponto 3\n' +
          '• Ponto 4\n\n' +
          'Boa sorte! 🎮',
        color: '#5865F2',
        accentEnabled: false,
        footer: '',
        timestamp: false,
        fields: [],
        authorName: '',
        authorIconUrl: '',
        authorUrl: '',
        imageUrl: '',
        thumbnailUrl: '',
        footerIconUrl: '',
        url: '',
      },
      buttons: [
        {
          id: `btn_start_${Date.now().toString(36)}`,
          enabled: true,
          label: 'Começar',
          style: 'Primary',
          emoji: '📝',
          url: '',
          replyTitle: 'Informação',
          replyDescription: 'Configure a resposta deste botão…',
          replyColor: '#5865F2',
        },
        {
          id: `btn_link_${Date.now().toString(36)}`,
          enabled: true,
          label: 'Abrir link',
          style: 'Secondary',
          emoji: '📖',
          url: 'https://nexussmp.com.br/',
          replyTitle: '',
          replyDescription: '',
          replyColor: '#5865F2',
        },
      ],
    }),
  },
  {
    id: 'announce',
    name: 'Anúncio',
    tagline: 'Comunicado geral',
    color: '#7C3AED',
    apply: () => ({
      mode: 'both',
      content: '@everyone',
      embed: {
        title: '📢 Anúncio importante',
        description:
          'Olá, comunidade!\n\nEscreva aqui o comunicado principal.\n\nObrigado pela atenção.',
        color: '#7C3AED',
        footer: 'Nexus Origins',
        timestamp: true,
        fields: [],
        authorName: '',
        authorIconUrl: '',
        authorUrl: '',
        imageUrl: '',
        thumbnailUrl: '',
        footerIconUrl: '',
        url: '',
      },
      buttons: [],
    }),
  },
  {
    id: 'event',
    name: 'Evento',
    tagline: 'Data e recompensas',
    color: '#F59E0B',
    apply: () => ({
      mode: 'embed',
      content: '',
      embed: {
        title: '🎉 Evento especial',
        description:
          'Participe do nosso próximo evento!\n\n**Quando:** dd/mm às hh:mm\n**Onde:** local / coordenadas\n**Como entrar:** instruções',
        color: '#F59E0B',
        footer: 'Equipe Nexus',
        timestamp: true,
        fields: [
          { name: 'Recompensa', value: 'Descreva os prêmios', inline: true },
          { name: 'Requisitos', value: 'Nível / VIP / etc.', inline: true },
        ],
        authorName: '',
        authorIconUrl: '',
        authorUrl: '',
        imageUrl: '',
        thumbnailUrl: '',
        footerIconUrl: '',
        url: '',
      },
      buttons: [
        {
          id: 'btn_site',
          enabled: true,
          label: 'Site',
          style: 'Secondary',
          url: 'https://nexussmp.com.br/',
          emoji: '🌐',
          replyTitle: '',
          replyDescription: '',
          replyColor: '#5865F2',
        },
      ],
    }),
  },
  {
    id: 'update',
    name: 'Atualização',
    tagline: 'Changelog curto',
    color: '#22C55E',
    apply: () => ({
      mode: 'embed',
      content: '',
      embed: {
        title: '🛠️ Atualização do servidor',
        description:
          'Novidades desta versão:\n\n• Item 1\n• Item 2\n• Item 3\n\nCorreções:\n• Bug fix…',
        color: '#22C55E',
        footer: 'Patch notes',
        timestamp: true,
        fields: [],
        authorName: 'Nexus Origins',
        authorIconUrl: '',
        authorUrl: '',
        imageUrl: '',
        thumbnailUrl: '',
        footerIconUrl: '',
        url: '',
      },
      buttons: [],
    }),
  },
  {
    id: 'plain',
    name: 'Texto simples',
    tagline: 'Sem embed',
    color: '#94A3B8',
    apply: () => ({
      mode: 'text',
      content: 'Escreva a mensagem aqui…',
      embed: emptyEmbed(),
      buttons: [],
    }),
  },
];

function emptyEmbed() {
  return {
    title: '',
    description: '',
    color: '#7C3AED',
    accentEnabled: false,
    url: '',
    authorName: '',
    authorIconUrl: '',
    authorUrl: '',
    footer: '',
    footerIconUrl: '',
    imageUrl: '',
    thumbnailUrl: '',
    timestamp: false,
    fields: [],
  };
}

function emptyField() {
  return { name: 'Campo', value: 'Valor', inline: false };
}

function emptyButton() {
  return {
    id: `btn_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    enabled: true,
    label: 'Novo botão',
    style: 'Primary',
    url: '',
    emoji: '',
    replyTitle: 'Informação',
    replyDescription: 'Conteúdo da resposta ao clicar…',
    replyColor: '#5865F2',
  };
}

function emptyDraft() {
  return {
    channelId: '',
    mode: 'both',
    content: '',
    embed: emptyEmbed(),
    buttons: [],
  };
}

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return emptyDraft();
    const data = JSON.parse(raw);
    return {
      ...emptyDraft(),
      ...data,
      embed: { ...emptyEmbed(), ...(data.embed || {}) },
      buttons: Array.isArray(data.buttons) ? data.buttons : [],
    };
  } catch {
    return emptyDraft();
  }
}

/**
 * Compositor profissional de mensagens / embeds via bot.
 */
const MessageComposerPanel = ({
  guildChannels = [],
  guildResourcesLoading = false,
  sending = false,
  message = '',
  error = '',
  lastResult = null,
  canEdit = true,
  onSend,
}) => {
  const [section, setSection] = useState('envio');
  const [draft, setDraft] = useState(emptyDraft);
  const [hydrated, setHydrated] = useState(false);
  const [channelQuery, setChannelQuery] = useState('');
  const descRef = useRef(null);

  useEffect(() => {
    setDraft(loadDraft());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      /* ignore quota */
    }
  }, [draft, hydrated]);

  const mode = draft.mode || 'both';
  const isV2 = mode === 'v2' || mode === 'container';
  const useText = !isV2 && (mode === 'text' || mode === 'both');
  const useEmbed = isV2 || mode === 'embed' || mode === 'both';
  const embed = draft.embed || emptyEmbed();

  const SECTIONS = [
    { id: 'envio', label: 'Envio' },
    { id: 'templates', label: 'Templates' },
    { id: 'texto', label: 'Texto' },
    { id: 'embed', label: isV2 ? 'Container' : 'Embed' },
    { id: 'botoes', label: `Botões (${draft.buttons.length})` },
  ];

  const filteredChannels = useMemo(() => {
    const q = channelQuery.trim().toLowerCase();
    if (!q) return guildChannels;
    return guildChannels.filter((ch) => {
      const name = String(ch.name || '').toLowerCase();
      const parent = String(ch.parentName || '').toLowerCase();
      return name.includes(q) || parent.includes(q) || String(ch.id).includes(q);
    });
  }, [guildChannels, channelQuery]);

  const channelsByParent = useMemo(() => {
    const groups = new Map();
    for (const ch of filteredChannels) {
      const key = ch.parentName || 'Sem categoria';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(ch);
    }
    return [...groups.entries()];
  }, [filteredChannels]);

  const selectedChannel = guildChannels.find(
    (ch) => String(ch.id) === String(draft.channelId)
  );

  const canSend = Boolean(draft.channelId) && !sending;

  const previewButtons = useMemo(
    () =>
      (draft.buttons || []).map((b) => {
        const hasUrl = Boolean(String(b.url || '').trim());
        return {
          ...b,
          style: hasUrl ? 'Secondary' : b.style || 'Primary',
          isLink: hasUrl,
          enabled: b.enabled !== false,
        };
      }),
    [draft.buttons]
  );

  const update = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const updateEmbed = (key, value) =>
    setDraft((prev) => ({
      ...prev,
      embed: { ...(prev.embed || emptyEmbed()), [key]: value },
    }));

  const insertDescSnippet = (snippet, { wrapSelection = false } = {}) => {
    const el = descRef.current;
    const current = embed.description || '';
    if (!el || typeof el.selectionStart !== 'number') {
      updateEmbed('description', `${current}${current ? '\n' : ''}${snippet}`);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = current.slice(start, end);
    const insert = wrapSelection
      ? snippet.replace('$1', selected || 'texto')
      : snippet;
    const next = current.slice(0, start) + insert + current.slice(end);
    updateEmbed('description', next.slice(0, 4096));
    requestAnimationFrame(() => {
      if (!descRef.current) return;
      const pos = start + insert.length;
      descRef.current.focus();
      descRef.current.setSelectionRange(pos, pos);
    });
  };

  const updateField = (index, key, value) => {
    const fields = (embed.fields || []).map((f, i) =>
      i === index ? { ...f, [key]: value } : f
    );
    updateEmbed('fields', fields);
  };

  const addField = () => {
    if ((embed.fields || []).length >= 25) return;
    updateEmbed('fields', [...(embed.fields || []), emptyField()]);
  };

  const removeField = (index) => {
    updateEmbed(
      'fields',
      (embed.fields || []).filter((_, i) => i !== index)
    );
  };

  const updateButton = (index, patch) => {
    update({
      buttons: draft.buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    });
  };

  const addButton = () => {
    if (draft.buttons.length >= 5) return;
    update({ buttons: [...draft.buttons, emptyButton()] });
  };

  const removeButton = (index) => {
    update({ buttons: draft.buttons.filter((_, i) => i !== index) });
  };

  const moveButton = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= draft.buttons.length) return;
    const next = [...draft.buttons];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    update({ buttons: next });
  };

  const applyTemplate = (tpl) => {
    const next = tpl.apply();
    setDraft((prev) => ({
      ...prev,
      ...next,
      channelId: prev.channelId,
      embed: { ...emptyEmbed(), ...(next.embed || {}) },
    }));
    setSection(next.mode === 'text' ? 'texto' : 'embed');
  };

  const clearDraft = () => {
    setDraft((prev) => ({
      ...emptyDraft(),
      channelId: prev.channelId,
    }));
  };

  const handleSend = () => {
    if (!onSend || !canSend) return;
    onSend({
      channelId: draft.channelId,
      mode: draft.mode,
      layout: isV2 ? 'v2' : 'classic',
      content: useText ? draft.content : '',
      embed: useEmbed ? embed : null,
      buttons: draft.buttons,
    });
  };

  return (
    <section className="admin-section msg-panel">
      <header className="admin-section__head">
        <h1>Enviar mensagem</h1>
        <p>
          Container V2 (banner, separador nativo e botões no card), embed
          clássica ou texto — com preview ao vivo
        </p>
      </header>

      {message && <p className="admin-success">{message}</p>}
      {error && (
        <div className="admin-alert" role="alert">
          {error}
        </div>
      )}

      {lastResult?.jumpUrl && (
        <div className="msg-result">
          <FaCheckCircle aria-hidden="true" />
          <span>
            Enviado em{' '}
            <strong>#{lastResult.channelName || 'canal'}</strong>
          </span>
          <a
            href={lastResult.jumpUrl}
            target="_blank"
            rel="noreferrer"
            className="admin-btn admin-btn--ghost admin-btn--sm"
          >
            Abrir no Discord <FaExternalLinkAlt aria-hidden="true" />
          </a>
        </div>
      )}

      <div className="msg-shell">
        <div className="msg-editor">
          <nav className="msg-tabs" aria-label="Seções do compositor">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`msg-tab${section === s.id ? ' is-active' : ''}`}
                onClick={() => setSection(s.id)}
              >
                {s.label}
              </button>
            ))}
          </nav>

          <div className="msg-editor__body admin-form">
            {section === 'envio' && (
              <div className="msg-section-pane">
                <div className="msg-mode-grid" role="radiogroup" aria-label="Tipo">
                  {MODES.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      role="radio"
                      aria-checked={mode === m.id}
                      className={`msg-mode${mode === m.id ? ' is-active' : ''}`}
                      onClick={() => update({ mode: m.id })}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                <label>
                  Buscar canal
                  <input
                    type="search"
                    value={channelQuery}
                    onChange={(e) => setChannelQuery(e.target.value)}
                    placeholder="Nome ou categoria…"
                    disabled={guildResourcesLoading}
                  />
                </label>

                <label>
                  Canal de destino
                  <select
                    value={draft.channelId || ''}
                    onChange={(e) => update({ channelId: e.target.value })}
                    disabled={guildResourcesLoading}
                  >
                    <option value="">
                      {guildResourcesLoading
                        ? 'Carregando canais…'
                        : 'Selecione um canal de texto'}
                    </option>
                    {channelsByParent.map(([parent, channels]) => (
                      <optgroup key={parent} label={parent}>
                        {channels.map((ch) => (
                          <option key={ch.id} value={ch.id}>
                            #{ch.name}
                            {ch.type === 5 ? ' (anúncios)' : ''}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                    {draft.channelId &&
                      !guildChannels.some(
                        (ch) => String(ch.id) === String(draft.channelId)
                      ) && (
                        <option value={draft.channelId}>
                          Canal salvo ({draft.channelId})
                        </option>
                      )}
                  </select>
                </label>

                {selectedChannel && (
                  <p className="admin-field-hint">
                    Destino: <strong>#{selectedChannel.name}</strong>
                    {selectedChannel.parentName
                      ? ` · ${selectedChannel.parentName}`
                      : ''}
                  </p>
                )}

                <ul className="msg-hint-row">
                  <li>
                    <strong>Container V2</strong>
                    <span>Banner + separador + botões no card</span>
                  </li>
                  <li>
                    <strong>Embed</strong>
                    <span>Formato clássico Discord</span>
                  </li>
                  <li>
                    <strong>Botões</strong>
                    <span>Link ou Primary · máx. 5</span>
                  </li>
                </ul>
              </div>
            )}

            {section === 'templates' && (
              <div className="msg-section-pane">
                <p className="admin-field-hint">
                  Modelos prontos. O canal selecionado é mantido.
                </p>
                <div className="msg-templates">
                  {TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      className="msg-template"
                      style={{ '--tpl-accent': tpl.color }}
                      onClick={() => applyTemplate(tpl)}
                    >
                      <div className="msg-template__swatch" aria-hidden="true">
                        <span className="msg-template__bar" />
                        <span className="msg-template__lines">
                          <i />
                          <i />
                          <i />
                        </span>
                      </div>
                      <header>
                        <p className="msg-template__tag">{tpl.tagline}</p>
                        <h3>{tpl.name}</h3>
                      </header>
                      <span className="msg-template__action">Aplicar</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {section === 'texto' && (
              <div className="msg-section-pane">
                {!useText && (
                  <p className="admin-field-hint">
                    O modo atual é só embed — o texto não será enviado. Mude em
                    Envio se quiser incluir.
                  </p>
                )}
                <label>
                  Conteúdo da mensagem
                  <textarea
                    rows={10}
                    value={draft.content || ''}
                    maxLength={2000}
                    disabled={!useText}
                    onChange={(e) => update({ content: e.target.value })}
                    placeholder="Mensagem normal do bot… Use **negrito**, *itálico*, `código`, menções @everyone / @here / <@id>"
                  />
                </label>
                <p className="admin-field-hint msg-counter">
                  {(draft.content || '').length} / 2000
                </p>
              </div>
            )}

            {section === 'embed' && (
              <div className="msg-section-pane">
                {!useEmbed && (
                  <p className="admin-field-hint">
                    O modo atual é só texto — a embed não será enviada.
                  </p>
                )}
                {isV2 && (
                  <p className="admin-field-hint">
                    Modo <strong>Container V2</strong>: Components V2 do Discord —
                    banner no topo, separadores nativos e botões <em>dentro</em> do
                    card (não é embed clássica).
                  </p>
                )}

                <fieldset disabled={!useEmbed} className="msg-fieldset">
                  <label>
                    Título
                    <input
                      type="text"
                      value={embed.title || ''}
                      maxLength={256}
                      onChange={(e) => updateEmbed('title', e.target.value)}
                      placeholder="Título"
                    />
                  </label>

                  <label>
                    Descrição
                    <div className="msg-md-toolbar" role="toolbar" aria-label="Atalhos da descrição">
                      <button
                        type="button"
                        className="msg-md-btn"
                        disabled={!useEmbed}
                        onClick={() =>
                          insertDescSnippet(
                            isV2
                              ? `\n\n${EMBED_SEPARATOR}\n\n`
                              : `\n\n${EMBED_SEPARATOR_VISUAL}\n\n`
                          )
                        }
                        title={
                          isV2
                            ? 'Separator nativo (Components V2)'
                            : 'Linha de separação em texto'
                        }
                      >
                        Separador
                      </button>
                      <button
                        type="button"
                        className="msg-md-btn"
                        disabled={!useEmbed}
                        onClick={() => insertDescSnippet('\n• ')}
                        title="Item de lista"
                      >
                        • Lista
                      </button>
                      <button
                        type="button"
                        className="msg-md-btn"
                        disabled={!useEmbed}
                        onClick={() =>
                          insertDescSnippet('**$1**', { wrapSelection: true })
                        }
                        title="Negrito"
                      >
                        Negrito
                      </button>
                      <button
                        type="button"
                        className="msg-md-btn"
                        disabled={!useEmbed}
                        onClick={() =>
                          insertDescSnippet(`\n\n**Importante**\n`)
                        }
                        title="Subtítulo Importante"
                      >
                        Seção
                      </button>
                    </div>
                    <textarea
                      ref={descRef}
                      rows={9}
                      value={embed.description || ''}
                      maxLength={4096}
                      onChange={(e) =>
                        updateEmbed('description', e.target.value)
                      }
                      placeholder={
                        isV2
                          ? 'Texto do container. Use Separador para a linha nativa entre seções.'
                          : 'Corpo da embed (markdown do Discord)'
                      }
                    />
                  </label>
                  <p className="admin-field-hint msg-counter">
                    {(embed.description || '').length} / 4096
                    {isV2 &&
                    String(embed.description || '').includes(EMBED_SEPARATOR)
                      ? ' · separator nativo'
                      : ''}
                  </p>

                  <label>
                    Imagem / banner (URL)
                    <input
                      type="url"
                      value={embed.imageUrl || ''}
                      onChange={(e) =>
                        updateEmbed('imageUrl', e.target.value)
                      }
                      placeholder={
                        isV2
                          ? 'https://… (banner no topo do container)'
                          : 'https://… (abaixo do texto na embed)'
                      }
                    />
                  </label>

                  <div className="msg-grid-2">
                    <label>
                      Cor {isV2 ? '(accent)' : 'da barra'}
                      <div className="msg-color">
                        <input
                          type="color"
                          value={
                            /^#[0-9A-Fa-f]{6}$/.test(embed.color || '')
                              ? embed.color
                              : '#7C3AED'
                          }
                          onChange={(e) => updateEmbed('color', e.target.value)}
                          aria-label="Cor"
                          disabled={isV2 && !embed.accentEnabled}
                        />
                        <input
                          type="text"
                          value={embed.color || ''}
                          onChange={(e) => updateEmbed('color', e.target.value)}
                          placeholder="#7C3AED"
                          disabled={isV2 && !embed.accentEnabled}
                        />
                      </div>
                    </label>

                    {isV2 ? (
                      <label className="admin-switch msg-switch-inline">
                        <span>
                          <strong>Barra lateral</strong>
                          <small>Accent (padrão: desligado)</small>
                        </span>
                        <input
                          type="checkbox"
                          checked={Boolean(embed.accentEnabled)}
                          onChange={(e) =>
                            updateEmbed('accentEnabled', e.target.checked)
                          }
                        />
                      </label>
                    ) : (
                      <label className="admin-switch msg-switch-inline">
                        <span>
                          <strong>Timestamp</strong>
                          <small>Data/hora no rodapé</small>
                        </span>
                        <input
                          type="checkbox"
                          checked={Boolean(embed.timestamp)}
                          onChange={(e) =>
                            updateEmbed('timestamp', e.target.checked)
                          }
                        />
                      </label>
                    )}
                  </div>

                  {!isV2 && (
                    <>
                      <label>
                        URL do título (opcional)
                        <input
                          type="url"
                          value={embed.url || ''}
                          onChange={(e) => updateEmbed('url', e.target.value)}
                          placeholder="https://…"
                        />
                      </label>

                      <details className="msg-accordion">
                        <summary>Autor, thumbnail e rodapé</summary>
                        <div className="msg-accordion__body">
                          <label>
                            Nome do autor
                            <input
                              type="text"
                              value={embed.authorName || ''}
                              maxLength={256}
                              onChange={(e) =>
                                updateEmbed('authorName', e.target.value)
                              }
                            />
                          </label>
                          <div className="msg-grid-2">
                            <label>
                              Ícone do autor (URL)
                              <input
                                type="url"
                                value={embed.authorIconUrl || ''}
                                onChange={(e) =>
                                  updateEmbed('authorIconUrl', e.target.value)
                                }
                                placeholder="https://…"
                              />
                            </label>
                            <label>
                              Link do autor
                              <input
                                type="url"
                                value={embed.authorUrl || ''}
                                onChange={(e) =>
                                  updateEmbed('authorUrl', e.target.value)
                                }
                                placeholder="https://…"
                              />
                            </label>
                          </div>
                          <label>
                            Thumbnail (URL)
                            <input
                              type="url"
                              value={embed.thumbnailUrl || ''}
                              onChange={(e) =>
                                updateEmbed('thumbnailUrl', e.target.value)
                              }
                              placeholder="https://…"
                            />
                          </label>
                          <label>
                            Rodapé
                            <input
                              type="text"
                              value={embed.footer || ''}
                              maxLength={2048}
                              onChange={(e) =>
                                updateEmbed('footer', e.target.value)
                              }
                            />
                          </label>
                          <label>
                            Ícone do rodapé (URL)
                            <input
                              type="url"
                              value={embed.footerIconUrl || ''}
                              onChange={(e) =>
                                updateEmbed('footerIconUrl', e.target.value)
                              }
                              placeholder="https://…"
                            />
                          </label>
                        </div>
                      </details>

                      <div className="msg-fields-head">
                        <h3>Campos ({(embed.fields || []).length}/25)</h3>
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          onClick={addField}
                          disabled={(embed.fields || []).length >= 25}
                        >
                          <FaPlus aria-hidden="true" /> Campo
                        </button>
                      </div>

                      {(embed.fields || []).map((field, index) => (
                        <div key={`field-${index}`} className="msg-field-card">
                          <div className="msg-grid-2">
                            <label>
                              Nome
                              <input
                                type="text"
                                value={field.name || ''}
                                maxLength={256}
                                onChange={(e) =>
                                  updateField(index, 'name', e.target.value)
                                }
                              />
                            </label>
                            <label className="admin-switch msg-switch-inline">
                              <span>
                                <strong>Inline</strong>
                                <small>Lado a lado</small>
                              </span>
                              <input
                                type="checkbox"
                                checked={Boolean(field.inline)}
                                onChange={(e) =>
                                  updateField(index, 'inline', e.target.checked)
                                }
                              />
                            </label>
                          </div>
                          <label>
                            Valor
                            <textarea
                              rows={3}
                              value={field.value || ''}
                              maxLength={1024}
                              onChange={(e) =>
                                updateField(index, 'value', e.target.value)
                              }
                            />
                          </label>
                          <button
                            type="button"
                            className="admin-btn admin-btn--danger admin-btn--sm"
                            onClick={() => removeField(index)}
                          >
                            <FaTrash aria-hidden="true" /> Remover campo
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </fieldset>
              </div>
            )}

            {section === 'botoes' && (
              <div className="msg-section-pane">
                <div className="msg-fields-head">
                  <h3>Botões ({draft.buttons.length}/5)</h3>
                  <button
                    type="button"
                    className="admin-btn admin-btn--ghost admin-btn--sm"
                    onClick={addButton}
                    disabled={draft.buttons.length >= 5}
                  >
                    <FaPlus aria-hidden="true" /> Botão
                  </button>
                </div>
                <p className="admin-field-hint">
                  Com URL → botão Link (abre o site). Sem URL → Primary/Secondary
                  com resposta ephemeral (o bot precisa tratar o clique{' '}
                  <code>pm:…</code>).
                </p>

                {draft.buttons.length === 0 && (
                  <p className="admin-field-hint">Nenhum botão ainda.</p>
                )}

                {draft.buttons.map((btn, index) => {
                  const hasUrl = Boolean(String(btn.url || '').trim());
                  return (
                    <div key={btn.id || index} className="msg-field-card">
                      <div className="msg-grid-2">
                        <label>
                          Rótulo
                          <input
                            type="text"
                            value={btn.label || ''}
                            maxLength={80}
                            onChange={(e) =>
                              updateButton(index, { label: e.target.value })
                            }
                          />
                        </label>
                        <label>
                          Emoji (opcional)
                          <input
                            type="text"
                            value={btn.emoji || ''}
                            maxLength={8}
                            onChange={(e) =>
                              updateButton(index, { emoji: e.target.value })
                            }
                            placeholder="📝"
                          />
                        </label>
                      </div>
                      <div className="msg-grid-2">
                        <label>
                          Estilo
                          <select
                            value={btn.style || 'Primary'}
                            onChange={(e) =>
                              updateButton(index, { style: e.target.value })
                            }
                            disabled={hasUrl}
                          >
                            {BUTTON_STYLES.map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          URL (opcional — vira Link)
                          <input
                            type="url"
                            value={btn.url || ''}
                            onChange={(e) =>
                              updateButton(index, { url: e.target.value })
                            }
                            placeholder="https://… (vazio = resposta ao clicar)"
                          />
                        </label>
                      </div>
                      {!hasUrl && (
                        <>
                          <label>
                            Título da resposta
                            <input
                              type="text"
                              value={btn.replyTitle || ''}
                              maxLength={256}
                              onChange={(e) =>
                                updateButton(index, {
                                  replyTitle: e.target.value,
                                })
                              }
                            />
                          </label>
                          <label>
                            Conteúdo da resposta (ephemeral)
                            <textarea
                              rows={4}
                              value={btn.replyDescription || ''}
                              maxLength={4000}
                              onChange={(e) =>
                                updateButton(index, {
                                  replyDescription: e.target.value,
                                })
                              }
                              placeholder="Mensagem privada ao clicar no botão…"
                            />
                          </label>
                        </>
                      )}
                      <div className="msg-btn-actions">
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          onClick={() => moveButton(index, -1)}
                          disabled={index === 0}
                          aria-label="Mover para cima"
                        >
                          <FaArrowUp />
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          onClick={() => moveButton(index, 1)}
                          disabled={index === draft.buttons.length - 1}
                          aria-label="Mover para baixo"
                        >
                          <FaArrowDown />
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn--danger admin-btn--sm"
                          onClick={() => removeButton(index)}
                        >
                          <FaTrash aria-hidden="true" /> Remover
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="msg-editor__footer">
            <button
              type="button"
              className="admin-btn admin-btn--ghost"
              onClick={clearDraft}
              disabled={sending}
            >
              <FaEraser aria-hidden="true" /> Limpar
            </button>
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              onClick={handleSend}
              disabled={!canSend || !canEdit}
            >
              <FaPaperPlane aria-hidden="true" />
              {sending
                ? 'Enviando…'
                : canEdit
                  ? 'Enviar no Discord'
                  : 'Sem permissão para enviar'}
            </button>
          </div>
        </div>

        <aside className="msg-preview">
          <div className="msg-preview__card">
            <div className="msg-preview__head">
              <h2 className="msg-preview-title">Preview ao vivo</h2>
              <p className="msg-preview-sub">
                {selectedChannel
                  ? `Como ficará em #${selectedChannel.name}`
                  : 'Selecione um canal para enviar'}
              </p>
            </div>
            <DiscordEmbedPreview
              content={useText ? draft.content : ''}
              embed={embed}
              showEmbed={useEmbed}
              buttons={previewButtons}
              layout={isV2 ? 'v2' : 'classic'}
              mentionUser={false}
            />
          </div>
        </aside>
      </div>
    </section>
  );
};

export default MessageComposerPanel;
